import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  BlockPAOCell,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
  blockItalicSecondaryNoteClass,
} from "../../designSystem/projectBlocks";
import { MEDIA_BASE } from "../../config/media";

const BASE = "/projects/AquaCast/images";
const VIDEO = `${MEDIA_BASE}/projects/AquaCast/video`;

/**
 * Placeholder architecture diagram — five-stage subsystem flow, rendered
 * inline as SVG so it adapts to dark mode and prints cleanly. Replace this
 * component with a <BlockFigure src="…architecture.png" /> the moment a
 * proper diagram exists in /public.
 */
function ArchitectureDiagram() {
  return (
    <div className="relative w-full aspect-[3/1] overflow-hidden bg-neutral-50 dark:bg-neutral-950">
      <svg
        viewBox="0 0 1200 400"
        className="absolute inset-0 w-full h-full text-black dark:text-white"
        aria-label="AquaCast subsystem flow: deploy, sample, resurface, recover, lab"
      >
        {/* Five columns: x = 60, 290, 520, 750, 980 (width 180 each) */}
        {[
          { x: 60, title: "Deploy", lines: ["Drone", "Release mechanism", "Payload < 1 kg"] },
          { x: 290, title: "Sample", lines: ["ESP32-C3 controller", "4–7 depth chambers", "Temp · turbidity · depth", "GPS + timestamp"] },
          { x: 520, title: "Resurface", lines: ["CO₂ cartridge", "Inflatable bladder", "High-visibility marker"] },
          { x: 750, title: "Recover", lines: ["Visual beacon", "GPS last-known", "Manual pickup"] },
          { x: 980, title: "Lab handoff", lines: ["Sealed bottles", "Metadata CSV/JSON", "Chain of custody"] },
        ].map((col, i) => (
          <g key={col.title}>
            <rect
              x={col.x}
              y={80}
              width={170}
              height={240}
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
            <text
              x={col.x + 12}
              y={108}
              className="font-mono"
              style={{ fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" }}
              fill="currentColor"
            >
              {String(i + 1).padStart(2, "0")} · {col.title}
            </text>
            <line
              x1={col.x + 12}
              y1={120}
              x2={col.x + 158}
              y2={120}
              stroke="currentColor"
              strokeWidth="0.5"
              opacity="0.4"
            />
            {col.lines.map((line, j) => (
              <text
                key={line}
                x={col.x + 12}
                y={150 + j * 22}
                className="font-mono"
                style={{ fontSize: 11 }}
                fill="currentColor"
                opacity="0.85"
              >
                {line}
              </text>
            ))}
          </g>
        ))}

        {/* Connecting arrows between stages */}
        {[
          [230, 460],
          [460, 690],
          [690, 920],
          [920, 1150 - 200], // visual: stop arrow before fifth box
        ].map(([x1, x2], i) => (
          <g key={i}>
            <line
              x1={x1}
              y1={200}
              x2={x2 - 8}
              y2={200}
              stroke="currentColor"
              strokeWidth="1"
            />
            <polygon
              points={`${x2 - 8},196 ${x2},200 ${x2 - 8},204`}
              fill="currentColor"
            />
          </g>
        ))}

        {/* Footer caption inside the diagram */}
        <text
          x={60}
          y={360}
          className="font-mono"
          style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}
          fill="currentColor"
          opacity="0.55"
        >
          Subsystem flow · placeholder schematic · swap for a final diagram when ready
        </text>
      </svg>
    </div>
  );
}

export default function AquaCastPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const processFrames = [
    `${BASE}/process_01.jpg`,
    `${BASE}/process_02.jpg`,
    `${BASE}/process_03.jpg`,
    `${BASE}/process_04.jpg`,
    `${BASE}/process_05.jpg`,
  ];
  const processCaps = [
    "Bench iteration 01",
    "Bench iteration 02",
    "Bench iteration 03",
    "Bench iteration 04",
    "Bench iteration 05",
  ];

  const demoFrames = [
    `${BASE}/demo_01.jpg`,
    `${BASE}/demo_02.jpg`,
    `${BASE}/demo_03.jpg`,
    `${BASE}/demo_04.jpg`,
    `${BASE}/demo_05.jpg`,
    `${BASE}/demo_06.jpg`,
  ];
  const demoCaps = [
    "Demo day 01",
    "Demo day 02",
    "Demo day 03",
    "Demo day 04",
    "Demo day 05",
    "Demo day 06",
  ];

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="AquaCast"
          description="A modular, drone-deployable, multi-depth near-shore water sampling system — physical lab-ready samples and automatic metadata in a single mission, without sending people out by boat."
          meta={[
            "Microsoft Research · UW GIX/MSTI",
            "Near-shore water monitoring",
            "ESP32-C3",
            "Hardware",
            "Field research",
            "2026",
          ]}
        />

        {/* 1 · INTRO --------------------------------------------------- */}
        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/prototype_01.jpg`}
                alt="AquaCast — final prototype"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Intro">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "AquaCast is a modular, drone-deployable water sampling system. In a single mission it collects four to seven discrete physical samples across the water column and pairs each one with GPS, timestamp, depth, temperature, and turbidity.",
              "It sits in the gap between manual boat-based sampling and sensor-only monitoring platforms: more capable than a surface-only drone sampler, simpler and lower-cost than a full underwater vehicle. The project was developed at UW GIX/MSTI in the context of a Microsoft Research problem framing on near-shore water monitoring.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/mockup_01.png`}
                  alt="AquaCast — concept mockup 1"
                  ratio="classic"
                  fit="contain"
                  caption="Concept · 01"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/mockup_02.png`}
                  alt="AquaCast — concept mockup 2"
                  ratio="classic"
                  fit="contain"
                  caption="Concept · 02"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* 2 · PROBLEM ------------------------------------------------- */}
        <BlockSection title="The problem">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Near-shore water monitoring still relies on slow, manual workflows: teams travel by boat, lower bottles on ropes, collect one sample at a time, log metadata by hand, and repeat the same route across many sites. That work is operationally heavy, exposes field staff to risk, and limits how often sites can actually be sampled.",
              "Sensor buoys and surface drone samplers fill part of the gap — continuous data, easier access — but they do not produce the physical samples that regulatory decisions, contaminant testing, and lab-based analysis still depend on. And neither approach captures how water quality varies vertically, where many of the most useful signals (temperature gradients, turbidity, biology, contamination) actually live.",
            ]}
          />
        </BlockSection>

        {/* 3 · SOLUTION REQUIREMENTS ----------------------------------- */}
        <BlockSection title="Solution requirements">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Six requirements drove every later design choice. They were derived directly from the field-workflow research — each one maps to a specific failure mode in the current sampling routine.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={4} className="p-4 lg:p-5">
                <BlockPAOCell
                  role="Req 01 · Physical sampling"
                  body="Collect actual lab-ready water samples — not just sensor data — so the output works inside regulatory, chemistry, and biology workflows."
                />
              </RecipeCell>
              <RecipeCell span={4} className="p-4 lg:p-5">
                <BlockPAOCell
                  role="Req 02 · Multi-depth"
                  body="Capture 4–7 discrete samples across the water column in a single mission, with each sample sealed and isolated from the others."
                />
              </RecipeCell>
              <RecipeCell span={4} className="!border-r-0 p-4 lg:p-5">
                <BlockPAOCell
                  role="Req 03 · Drone-deployable"
                  body="Operate within drone payload limits and integrate cleanly with deployment + release, removing the need for boat trips."
                />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={4} className="p-4 lg:p-5">
                <BlockPAOCell
                  role="Req 04 · Reliable retrieval"
                  body="Resurface predictably and stay easy to locate visually — a missed pickup costs the device, the samples, and the mission."
                />
              </RecipeCell>
              <RecipeCell span={4} className="p-4 lg:p-5">
                <BlockPAOCell
                  role="Req 05 · Automatic metadata"
                  body="Log GPS, timestamp, depth, temperature, and turbidity per sample, so every bottle ships with the context the lab needs."
                />
              </RecipeCell>
              <RecipeCell span={4} className="!border-r-0 p-4 lg:p-5">
                <BlockPAOCell
                  role="Req 06 · Modular and field-robust"
                  body="Survive waterproofing, pressure, repeated reuse, and non-engineer handling — and stay simple enough to evolve later."
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* 4 · ARCHITECTURE DIAGRAM ------------------------------------ */}
        <BlockSection title="Architecture diagram">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Five subsystems, in mission order — deploy, sample, resurface, recover, lab handoff. Each cell lists the dominant constraints and components for that stage. The diagram below is a placeholder schematic; a final version will replace it once produced.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <ArchitectureDiagram />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* 5 · PROTOTYPES ON WORK (includes process) ------------------- */}
        <BlockSection title="Prototypes on work">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The process ran in three overlapping tracks: workflow research with King County, UW, and Friday Harbor Labs to ground the brief; bench prototyping of the sealing, sampling, and electronics; and submersion testing to reveal what only shows up in real water.",
              "Early enclosure tests failed in informative ways. Waterproofing was the single biggest risk surfaced, and the failures pushed us to treat sealing as a core system — material selection, gasket geometry, cable penetrations, sensor ports, sample inlet and outlet — rather than a finishing step. The bench iterations below trace that loop: each version targeted one failure mode from the prior round.",
              "The current build runs on an ESP32-C3 with battery-powered operation, BLE for short-range data, and on-device metadata logging. The philosophy was deliberately conservative — gather raw or lightly processed measurements, push them to a paired computer for visualisation, and keep the device side of the system as small as it can be.",
            ]}
          />

          {/* Iteration sequence */}
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <ImageSequence
                  images={processFrames}
                  captions={processCaps}
                  label="Bench iterations"
                  ratio="video"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>

          {/* Final prototype diptych */}
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/prototype_02.jpg`}
                  alt="AquaCast — final prototype, view 2"
                  ratio="classic"
                  fit="cover"
                  caption="Prototype · view 2"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/prototype_03.jpg`}
                  alt="AquaCast — final prototype, view 3"
                  ratio="classic"
                  fit="cover"
                  caption="Prototype · view 3"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* 6 · DEMO SETUP ---------------------------------------------- */}
        <BlockSection title="Demo setup">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "AquaCast was presented at the program's demo day — final prototype on the table, drone-deployment video on screen, poster on the wall, and a sampling-as-a-service framing for municipal water teams, environmental consultancies, and university research groups.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <div className="relative w-full aspect-video bg-neutral-100 dark:bg-neutral-900 overflow-hidden">
                  <video
                    className="absolute inset-0 w-full h-full object-cover"
                    src={`${VIDEO}/drone_team.mp4`}
                    controls
                    playsInline
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <ImageSequence
                  images={demoFrames}
                  captions={demoCaps}
                  label="Demo day · gallery"
                  ratio="video"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/poster.jpg`}
                  alt="AquaCast — final poster"
                  ratio="banner"
                  fit="contain"
                  caption="Final poster"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>

          <p className={blockItalicSecondaryNoteClass}>
            Project conducted at UW GIX/MSTI in the context of a Microsoft Research-provided problem framing.
          </p>
        </BlockSection>
      </div>
    </main>
  );
}
